import type { BackendModule } from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { matrixLocaleResources } from './staticLocaleImports/resourcesMatrix';

// The literal i18next itself falls back to, set in getOptions (./settings) — not the exported
// `fallbackLng`, which is 'en-US'. `en` is also the only language kept complete on disk.
const COMPLETE_LANGUAGE = 'en';

/**
 * Namespace loader for the matrix runner, swapped in for `./backend` through
 * `turbopack.resolveAlias` in apps/matrix/next.config.ts. See `./backend` for why.
 */
export const localeBackend: BackendModule = resourcesToBackend(
  // must stay at two parameters: resources-to-backend dispatches on the resolver's arity and a
  // third one silently flips it into callback mode
  (language: string, namespace: string) => {
    const load = matrixLocaleResources[language]?.[namespace];

    if (load) return load();

    // Most languages are only partly translated, so a namespace missing for one of them is
    // routine rather than a registry gap. Resolving empty is quieter than what the glob backend
    // did — its import() rejected, taking i18next's failed-load path — and lands on `en` either
    // way.
    if (language !== COMPLETE_LANGUAGE) return Promise.resolve({});

    // A gap in `en` is the real failure. i18next treats an empty-but-resolved namespace as
    // loaded, so it would quietly fall through to fallbackNS (`common`) and render whatever
    // generic key happened to collide. Throwing routes it through the backend's error path
    // instead, and i18next only logs those when `debug` is on, hence the console.error.
    const message = `[i18n] "${namespace}" is not in the matrix locale registry — regenerate packages/i18n/src/staticLocaleImports/resourcesMatrix.ts, or the namespace is deny-listed as builder-only (FORBIDDEN_NAMESPACES in generateMatrixResources.mjs) and this code should use its runner counterpart`;
    console.error(message);
    throw new Error(message);
  },
);
